// client/src/pages/OrderDetailPage.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Container, Box, Typography, Chip, Card, CardContent, Stack, Divider, Alert, CircularProgress,
  Grid, Button, Breadcrumbs, Stepper, Step, StepLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { formatCurrency, vatWasAddedOnTop } from '../utils/currency';
import { getImageUrl, API_BASE_URL } from '../utils/media';

const statusColor = { Pending: 'warning', Shipped: 'info', Completed: 'success', Cancelled: 'default' };
const paymentColor = { unpaid: 'warning', paid: 'success', failed: 'error' };
const TRACKING_STEPS = ['Pending', 'Shipped', 'Completed'];
const RETURN_REASONS = ['Item damaged/defective', 'Wrong item received', 'No longer needed', 'Item not as described', 'Other'];

const OrderDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payingAgain, setPayingAgain] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnForm, setReturnForm] = useState({ reason: '', description: '' });
  const [returnSubmitted, setReturnSubmitted] = useState(false);

  const paymentBanner = searchParams.get('payment'); // 'success' | 'failed'

  const fetchOrderDetails = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to fetch order details.');
      }
      setOrder(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  // A guest order (no account) is still viewable — the request just carries no token.
  useEffect(() => { fetchOrderDetails(); }, [fetchOrderDetails]);

  const handlePayAgain = async () => {
    setPayingAgain(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ orderId: order.id }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start payment.');
      navigate(data.redirectPath);
    } catch (err) {
      setError(err.message);
      setPayingAgain(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this order? This cannot be undone.')) return;
    setCancelling(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${id}/cancel`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to cancel order.');
      fetchOrderDetails();
    } catch (err) {
      alert(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitReturn = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${id}/return-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(returnForm),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to submit return request.');
      setReturnDialogOpen(false);
      setReturnSubmitted(true);
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <Container sx={{ py: 4 }}><CircularProgress size={28} /></Container>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!order) return <Container sx={{ py: 4 }}><Typography>Order not found.</Typography></Container>;

  const hasShipping = order.shipping_address_line1;
  const hasTracking = order.tracking_number;
  const activeStepIndex = order.status === 'Cancelled' ? -1 : TRACKING_STEPS.indexOf(order.status);
  const canCancel = Boolean(token) && order.status === 'Pending';
  const canRequestReturn = Boolean(token) && order.payment_status === 'paid' && !returnSubmitted;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2, overflowX: 'auto', whiteSpace: 'nowrap', '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}
      >
        <Link to="/profile" style={{ color: 'inherit', textDecoration: 'none' }}>My Account</Link>
        <Link to="/profile" style={{ color: 'inherit', textDecoration: 'none' }}>My Orders</Link>
        <Typography color="text.primary">Order #{order.id}</Typography>
      </Breadcrumbs>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h4" fontWeight={700}>Order Details</Typography>
        <Button component={Link} to={`/orders/${order.id}/invoice`} startIcon={<ReceiptLongOutlinedIcon />} variant="outlined">
          View Invoice
        </Button>
      </Stack>

      {paymentBanner === 'success' && <Alert severity="success" sx={{ mb: 2 }}>Payment successful! Thank you for your order.</Alert>}
      {paymentBanner === 'failed' && <Alert severity="error" sx={{ mb: 2 }}>Payment failed. You can try again below.</Alert>}
      {returnSubmitted && <Alert severity="success" sx={{ mb: 2 }}>Return request submitted — we'll be in touch.</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={4} flexWrap="wrap" alignItems="center">
            <Typography><strong>Order ID:</strong> #{order.id}</Typography>
            <Typography><strong>Date:</strong> {new Date(order.created_at).toLocaleDateString()}</Typography>
            <Typography><strong>Total:</strong> {formatCurrency(order.total_amount)}</Typography>
            <Box><strong>Status:</strong> <Chip label={order.status} color={statusColor[order.status] || 'default'} size="small" sx={{ ml: 1 }} /></Box>
            <Box><strong>Payment:</strong> <Chip label={order.payment_status} color={paymentColor[order.payment_status] || 'default'} size="small" sx={{ ml: 1, textTransform: 'capitalize' }} /></Box>
            <Chip
              icon={order.fulfillment_type === 'collection' ? <StorefrontOutlinedIcon /> : <LocalShippingIcon />}
              label={order.fulfillment_type === 'collection' ? 'Collection' : 'Delivery'}
              size="small" variant="outlined"
            />
          </Stack>

          {order.status !== 'Cancelled' && (
            <Stepper activeStep={activeStepIndex} sx={{ mt: 3, mb: 1 }} alternativeLabel>
              {TRACKING_STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>
          )}

          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2 }}>
            {order.payment_status !== 'paid' && order.status !== 'Cancelled' && (
              <Button variant="contained" size="small" onClick={handlePayAgain} disabled={payingAgain}>
                {payingAgain ? 'Starting payment...' : 'Pay Now'}
              </Button>
            )}
            {canCancel && (
              <Button variant="outlined" color="error" size="small" onClick={handleCancel} disabled={cancelling}>
                {cancelling ? 'Cancelling...' : 'Cancel Order'}
              </Button>
            )}
            {canRequestReturn && (
              <Button variant="outlined" size="small" onClick={() => setReturnDialogOpen(true)}>
                Request Return / Refund
              </Button>
            )}
          </Stack>

          {(order.points_earned > 0 || order.points_redeemed > 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {order.points_redeemed > 0 && `Redeemed ${order.points_redeemed} loyalty points. `}
              {order.points_earned > 0 && `You'll earn ${order.points_earned} loyalty points once this order is paid.`}
            </Typography>
          )}
        </CardContent>
      </Card>

      {order.statusHistory && order.statusHistory.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Order History</Typography>
            <Stack spacing={1}>
              {order.statusHistory.map((h, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2">
                    <strong>{h.status}</strong>{h.note ? ` — ${h.note}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{new Date(h.created_at).toLocaleString()}</Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {hasShipping && (
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Shipping Address</Typography>
                <Typography variant="body2">{order.shipping_name}</Typography>
                <Typography variant="body2">{order.shipping_address_line1}</Typography>
                {order.shipping_address_line2 && <Typography variant="body2">{order.shipping_address_line2}</Typography>}
                <Typography variant="body2">
                  {order.shipping_city}{order.shipping_province ? `, ${order.shipping_province}` : ''} {order.shipping_postal_code}
                </Typography>
                <Typography variant="body2">{order.shipping_country}</Typography>
                {order.shipping_phone && <Typography variant="body2">{order.shipping_phone}</Typography>}
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <LocalShippingIcon fontSize="small" color={hasTracking ? 'primary' : 'disabled'} />
                <Typography variant="subtitle1" fontWeight={700}>Shipping Status</Typography>
              </Stack>
              {hasTracking ? (
                <>
                  <Typography variant="body2"><strong>Carrier:</strong> {order.carrier_name}</Typography>
                  <Typography variant="body2"><strong>Tracking #:</strong> {order.tracking_number}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Shipped {new Date(order.shipped_at).toLocaleDateString()}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">Not shipped yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" fontWeight={700} gutterBottom>Items in this Order</Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {order.items.map((item) => (
          <Card key={item.productId} variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box component="img" src={getImageUrl(item.imageUrl)} alt={item.name} sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography fontWeight={600}>{item.name}</Typography>
                {item.variant_label && (
                  <Typography variant="body2" color="text.secondary">Option: {item.variant_label}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">Quantity: {item.quantity}</Typography>
                <Typography variant="body2" color="text.secondary">Price: {formatCurrency(item.price)}</Typography>
              </Box>
              <Typography variant="h6">{formatCurrency(item.quantity * item.price)}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1} sx={{ maxWidth: 300, ml: 'auto' }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography>{formatCurrency(order.subtotal ?? order.total_amount)}</Typography>
            </Stack>
            {Number(order.discount_amount) > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography color="success.main">Discount {order.coupon_code ? `(${order.coupon_code})` : ''}</Typography>
                <Typography color="success.main">-{formatCurrency(order.discount_amount)}</Typography>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Shipping</Typography>
              <Typography>{Number(order.shipping_fee) === 0 ? 'Free' : formatCurrency(order.shipping_fee)}</Typography>
            </Stack>
            {order.vat_amount != null && vatWasAddedOnTop(order) && (
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">VAT (15%)</Typography>
                <Typography>{formatCurrency(order.vat_amount)}</Typography>
              </Stack>
            )}
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" fontWeight={700}>Total</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(order.total_amount)}</Typography>
            </Stack>
            {order.vat_amount != null && !vatWasAddedOnTop(order) && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Includes VAT (15%)</Typography>
                <Typography variant="body2" color="text.secondary">{formatCurrency(order.vat_amount)}</Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={returnDialogOpen} onClose={() => setReturnDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Request a Return / Refund</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select label="Reason" fullWidth value={returnForm.reason}
              onChange={(e) => setReturnForm({ ...returnForm, reason: e.target.value })}
            >
              {RETURN_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField
              label="Additional details (optional)" fullWidth multiline minRows={3}
              value={returnForm.description}
              onChange={(e) => setReturnForm({ ...returnForm, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitReturn} disabled={!returnForm.reason}>Submit</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderDetailPage;
